<template>
    <div
        v-if="!group.deleted"
        class="px-4 py-1 border border-gray-100 rounded shadow-sm hover:shadow transition duration-150"
    >
        <modal @close="closeUpdateModal" :closable="!updating" :open="updateModalOpen">
            <div slot="header">
                <h2 class="pt-2 text-xl font-semibold text-red-600">
                    {{ $t('Update Chat Group') }}
                </h2>
            </div>
            <div slot="content">
                <form class="mt-4" @submit.prevent="updateChatGroup">
                    <p v-if="!!updateError" class="my-2 error-msg">{{ updateError }}</p>
                    <div class="mt-5">
                        <span class="uppercase tracking-widest text-xs">Members</span>
                        <v-select
                            :disabled="updating || loading"
                            class="style-select"
                            label="name"
                            @search="searchMembers"
                            :placeholder="$t('Select Members (Upto 5)')"
                            :options="searchMemberList"
                            v-model="formMembers"
                            multiple
                            :selectable="() => formMembers.length < 5"
                        >
                            <template v-slot:option="option">
                                <div class="flex items-center mb-1">
                                    <img
                                        :src="parseUrl(option.photoURL)"
                                        class="object-cover h-8 w-8 rounded-full mr-1"
                                    />
                                    <span>{{ option.name }}</span>
                                </div>
                            </template>
                        </v-select>
                    </div>
                    <div class="mt-5">
                        <span class="uppercase tracking-widest text-xs">Moderators</span>
                        <v-select
                            :disabled="updating || loading"
                            class="style-select"
                            label="name"
                            @search="searchMembers"
                            :placeholder="$t('Select Moderators (Upto 5)')"
                            :options="searchMemberList"
                            v-model="formModerators"
                            multiple
                            :selectable="() => formModerators.length < 5"
                        >
                            <template v-slot:option="option">
                                <div class="flex items-center mb-1">
                                    <img
                                        :src="parseUrl(option.photoURL)"
                                        class="object-cover h-8 w-8 rounded-full mr-1"
                                    />
                                    <span>{{ option.name }}</span>
                                </div>
                            </template>
                        </v-select>
                    </div>
                    <div class="mt-5 flex flex-col space-y-1">
                        <span class="uppercase tracking-widest text-xs">{{
                            $t('Group Name')
                        }}</span>
                        <input v-model="formGroupName" class="py-2 px-3 form-input" />
                    </div>
                    <div class="px-2 mt-6 mb-2 flex justify-between">
                        <Button
                            :disabled="updating"
                            @click="closeUpdateModal"
                            class="bg-gray-200 hover:bg-white hover:border-gray-800 border border-transparent px-6 py-2 rounded"
                            type="button"
                            :label="$t('Cancel')"
                        />
                        <Button
                            :disabled="updating"
                            :loading="loading"
                            class="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded"
                            :label="$t('Yes Update')"
                        />
                    </div>
                </form>
            </div>
        </modal>
        <modal @close="closeDeleteModal" :closable="!deleting" :open="deleteModalOpen">
            <div slot="header">
                <h2 class="pt-2 text-xl font-semibold text-red-600">
                    {{ $t('Delete Request') }}
                </h2>
            </div>
            <div slot="content">
                <form class="mt-4" @submit.prevent="deleteChatGroup">
                    <p class="bg-red-100 rounded-sm px-3 py-4">
                        Are you sure you want to delete this chat Group: id: {{ group.id }}
                        <br />
                        <span class="uppercase tracking-wide text-sm font-medium"
                            >This action cannot be reversed!!</span
                        >
                    </p>
                    <p v-if="!!deleteError" class="my-2 error-msg">{{ deleteError }}</p>
                    <div class="px-2 mt-6 mb-2 flex justify-between">
                        <Button
                            :disabled="deleting"
                            @click="closeDeleteModal"
                            class="bg-gray-200 hover:bg-white hover:border-gray-800 border border-transparent px-6 py-2 rounded"
                            type="button"
                            :label="$t('Cancel')"
                        />
                        <Button
                            :disabled="deleting"
                            :loading="deleting"
                            class="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded"
                            :label="$t('Yes Delete')"
                        />
                    </div>
                </form>
            </div>
        </modal>
        <div class="border border-gray-200 px-2 py-1 rounded w-full">
            <div class="pl-2 flex justify-between">
                <h2 v-if="group.name" class="text-sm text-gray-800 font-medium">
                    {{ group.name }}
                </h2>
                <h2 v-else class="text-xs text-gray-300 font-medium uppercase tracking-widest">
                    Members
                </h2>
                <div class="flex items-center justify-end gap-2">
                    <button
                        @click="updateModalOpen = true"
                        class="flex items-center text-gray-600 border-transparent hover:bg-gray-600 border hover:border-gray-600 hover:text-white text-xs px-3 py-px rounded-sm overflow-hidden"
                    >
                        <svg
                            class="w-4 h-4 text-gray-400 mr-1"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            ></path>
                        </svg>
                        {{ $t('Update') }}
                    </button>
                    <button
                        @click="deleteModalOpen = true"
                        class="flex items-center text-red-600 border-transparent hover:bg-red-600 border hover:border-red-600 hover:text-white text-xs px-3 py-px rounded-sm overflow-hidden"
                    >
                        <svg
                            class="w-4 h-4 group-hover:text-red-100 text-red-300 mr-1"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            ></path>
                        </svg>
                        {{ $t('Delete') }}
                    </button>
                </div>
            </div>
            <div class="pl-2 pb-2">
                <ul class="pt-0.5 flex flex-wrap -mr-3">
                    <li
                        v-for="item in Object.values(group.members || {})"
                        :key="item.id"
                        class="text-xs text-gray-900 font-medium"
                    >
                        <router-link
                            :to="`/users/${item.id}`"
                            class="mr-3 flex items-center bg-gray-50 hover:bg-white border border-transparent hover:border-gray-400 rounded px-1"
                        >
                            <img
                                loading="lazy"
                                class="object-cover w-7 h-7 flex-shrink-0 rounded-full overflow-hidden"
                                :src="parseUrl(item.photoURL)"
                            />
                            <span class="ml-1"> {{ item.name }}</span>
                        </router-link>
                    </li>
                </ul>
            </div>
            <div class="border-t border-gray-200 px-2 pt-3 flex items-center">
                <h2 class="text-xs text-gray-300 font-medium">(Moderators)</h2>
                <ul class="inline-flex flex-wrap">
                    <li v-if="moderators.length === 0">
                        <span class="ml-1 block text-xs font-medium text-gray-500">None</span>
                    </li>
                    <li v-for="item in moderators" :key="item.id" class="text-xs text-gray-900">
                        <router-link
                            :to="`/users/${item.id}`"
                            class="flex items-center bg-gray-50 hover:bg-white border border-transparent hover:border-gray-400 rounded px-1"
                        >
                            <span class=""> {{ item.name }}</span>
                        </router-link>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import api from '@/lib/api'
export default {
    name: 'ChatGroupCard',
    props: ['group'],
    data() {
        return {
            loading: false,
            deleteModalOpen: false,
            deleteError: null,
            deleting: false,
            updateModalOpen: false,
            updateError: null,
            updating: false,
            searchMemberList: [],
            formGroupName: '',
            formMembers: [],
            formModerators: [],
        }
    },
    mounted() {
        this.formMembers = this.members
        this.formModerators = this.moderators
        this.formGroupName = this.group.name
    },
    computed: {
        moderators() {
            return Object.values(this.group.moderators || {})
        },
        members() {
            return Object.values(this.group.members || {})
        },
        allNames() {
            const names = Object.values(this.group.members || {}).map(m => m.name)
            return names.join(',   ')
        },
    },
    methods: {
        searchMembers: _.debounce(async function(search, loading) {
            if (!search || search === '') return
            loading(true)
            const users = await api.searchUsersByName(search)
            this.searchMemberList = users
            loading(false)
        }, 200),
        parseUrl(url) {
            if (!url) return url
            if (url.startsWith('http')) {
                return url
            }
            return `https://cdn.giv.link/${url}`
        },
        closeUpdateModal() {
            this.updateModalOpen = false
            this.updateError = null
        },
        closeDeleteModal() {
            this.deleteModalOpen = false
            this.deleteError = null
            this.formModerators = this.moderators
            this.formGroupName = this.group.name
        },
        async updateChatGroup() {
            this.updating = true
            try {
                const payload = {
                    members: this.formMembers.map(m => m.id),
                    moderators: this.formModerators.map(m => m.id),
                }
                if (this.formGroupName && this.group.name !== this.formGroupName) {
                    payload.name = this.formGroupName
                }
                const newData = { moderators: {}, members: {}, name: this.formGroupName }
                this.formModerators.forEach(i => (newData.moderators[i.id] = i))
                this.formMembers.forEach(i => (newData.members[i.id] = i))
                await api.updateChatGroup(this.group.id, payload)
                this.updateModalOpen = false
                this.updateError = null
                this.group = { ...this.group, ...newData }
            } catch (err) {
                this.updateError = err.message
            }
            this.updating = false
        },
        async deleteChatGroup() {
            this.deleting = true
            try {
                await api.deleteChatGroup(this.group.id)
                this.deleteModalOpen = false
                this.group.deleted = true
            } catch (err) {
                this.deleteError = err.message
            }
            this.deleting = false
        },
    },
}
</script>
